import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { SlColumn, SlRow, StacklineColor, useStacklineTheme } from '@stackline/ui';
import queryString from 'qs';
import React, { useState, useMemo, useCallback } from 'react';
import { Segment } from 'sl-api-connector';
import ElementCard from 'src/components/BeaconV2Components/ElementCard/ElementCard';
import GenericCardLoading from 'src/components/BeaconV2Components/GenericCardLoading/GenericCardLoading';
import SplineChartV2, { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import { useComparisonWeekIds } from 'src/components/BeaconV2Components/SplineChartV2/useComparisonWeekIds';
import { useMetricByWeekIdV2 } from 'src/components/BeaconV2Components/SplineChartV2/useMetricByWeekIdV2';
import {
  getColorForMetricChangeValue,
  getIconForMetricChange
} from 'src/components/BeaconV2Components/utils/chartStyles';
import { BEACON_PRO_ENTITY_CARD_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { getAppName, getAtlasSalesV2Treatment } from 'src/utils/app';
import { AppName } from 'sl-api-connector';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { extractQueryConditions } from 'src/utils/segments';
import { SegmentMiniButton, TopicsMiniButton } from './MiniButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { useAtlasSegmentData } from './hooks/useAtlasSegmentData';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { ATLAS_SUBTABS, ATLAS_TABS } from 'src/components/AtlasV2Components/AtlasV2Sidebar/useAtlasV2Routes';
import { Link } from 'react-router-dom';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { timestampToWeekId, getLastWeekIdOfYear, yearPartOfWeekId } from 'src/utils/dateUtils';

interface SegmentCardProps {
  percentChange: number;
  displayName: string;
  subtitle: string;
  metricTotal: string | null;
  /** Timestamp-based data for backwards compatibility (Atlas) */
  primaryData?: number[][];
  /** Timestamp-based data for backwards compatibility (Atlas) */
  secondaryData?: number[][];
  /** Pre-built SplineChartV2 series (preferred for Beacon) */
  primarySeries?: SplineChartV2Series;
  /** Pre-built SplineChartV2 series (preferred for Beacon) */
  comparisonSeries?: SplineChartV2Series;
  /** Category overrides for YTD + Prior Year mode */
  categoryStartWeekId?: number;
  categoryEndWeekId?: number;
  segmentId: string;
  savedSearch: Segment['segment'];
  segmentUserId: string;
  metricType: METRICTYPE;
  handleDelete: () => void;
  metricDisplayName?: string;
  color?: StacklineColor;
  isTopicSegment?: boolean;
  isPending?: boolean;
  isLoading?: boolean;
}

const SegmentCardTitle = styled('p')(() => ({
  whiteSpace: 'pre-wrap',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': '3',
  display: '-webkit-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 500,
  fontSize: '18px',
  margin: 0
}));

const SegmentCard = React.memo(
  ({
    segmentId,
    percentChange,
    displayName,
    subtitle,
    metricTotal,
    primaryData,
    secondaryData,
    primarySeries: propPrimarySeries,
    comparisonSeries: propComparisonSeries,
    categoryStartWeekId: propCategoryStartWeekId,
    categoryEndWeekId: propCategoryEndWeekId,
    savedSearch,
    segmentUserId,
    metricType,
    handleDelete,
    metricDisplayName = 'Retail Sales',
    isTopicSegment = false,
    isPending = false,
    isLoading = false
  }: SegmentCardProps) => {
    const [hovered, setHovered] = useState(false);
    const theme = useStacklineTheme();
    const formatMetric = useMetricFormatter();
    const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
    const { userId } = useAppSelector((state) => state.user.session);

    // Get comparison week IDs to detect YTD + Prior Year mode (only needed for timestamp-based data)
    const { isYTDWithPriorYear, comparisonStartWeekId, comparisonEndWeekId } = useComparisonWeekIds();

    // For YTD + Prior Year, expand categories to include full prior year range
    // Use prop overrides if provided (from useMetricByWeekIdV2), otherwise calculate
    const categoryOverrides = useMemo(() => {
      // If category overrides are provided via props, use them
      if (propCategoryStartWeekId && propCategoryEndWeekId) {
        return {
          categoryStartWeekId: propCategoryStartWeekId,
          categoryEndWeekId: propCategoryEndWeekId
        };
      }
      // Otherwise calculate from comparison week IDs (for timestamp-based data)
      if (isYTDWithPriorYear && comparisonStartWeekId && comparisonEndWeekId) {
        const comparisonYear = yearPartOfWeekId(comparisonStartWeekId);
        return {
          categoryStartWeekId: comparisonStartWeekId,
          categoryEndWeekId: getLastWeekIdOfYear(comparisonYear)
        };
      }
      return {};
    }, [propCategoryStartWeekId, propCategoryEndWeekId, isYTDWithPriorYear, comparisonStartWeekId, comparisonEndWeekId]);

    // Use pre-built series if provided, otherwise convert timestamp-based data
    const primarySeries: SplineChartV2Series = useMemo(() => {
      if (propPrimarySeries) {
        return propPrimarySeries;
      }
      // Convert timestamp-based data to weekId-based format for SplineChartV2
      return {
        id: 'primary',
        name: 'Primary',
        data: (primaryData || [])
          .map((point) => ({
            weekId: timestampToWeekId(point[0]),
            value: point[1]
          }))
          .sort((a, b) => a.weekId - b.weekId)
      };
    }, [propPrimarySeries, primaryData]);

    // Use pre-built series if provided, otherwise convert timestamp-based data
    const comparisonSeries: SplineChartV2Series | undefined = useMemo(() => {
      if (propComparisonSeries) {
        return propComparisonSeries;
      }
      // Convert timestamp-based data to weekId-based format
      if (!secondaryData || secondaryData.length === 0) {
        return undefined;
      }
      return {
        id: 'comparison',
        name: 'Comparison',
        data: secondaryData
          .map((point) => ({
            weekId: timestampToWeekId(point[0]),
            value: point[1]
          }))
          .sort((a, b) => a.weekId - b.weekId)
      };
    }, [propComparisonSeries, secondaryData]);

    // Show loading UI when either pending or loading data
    const showLoadingUI = isPending || isLoading;
    // const showLoadingUI = true;

    // Beacon will have their own Topic card
    const getSegmentTab = (): string => {
      if (isTopicSegment) {
        return ATLAS_TABS.TRAFFIC;
      }

      if (getAtlasSalesV2Treatment() === 'off') {
        return getAppName() === AppName.Beacon ? BEACON_TABS.SUMMARY : ATLAS_TABS.SUMMARY;
      }

      return ATLAS_TABS.SUMMARY_V2;
    };

    const getSegmentSubtab = (): string => {
      if (getAppName() === AppName.Beacon) {
        return BEACON_SUBTABS.KEY_METRICS;
      }

      if (isTopicSegment) {
        return ATLAS_SUBTABS.TOTAL_CLICKS;
      }

      return 'all';
    };

    const buildSegmentUrl = (): string => {
      const basePath = isTopicSegment ? 'searchtermlist' : 'segment';
      const tab = getSegmentTab();
      const subtab = getSegmentSubtab();

      return `/${basePath}/${segmentId}${searchParams}&tab=${tab}&subtab=${subtab}`;
    };

    return (
      <div
        style={{
          width: BEACON_PRO_ENTITY_CARD_WIDTH,
          height: '484px',
          border: `1px solid ${theme.colors.primaryGray}`,
          borderRadius: theme.spacing.sm,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden',
          position: 'relative'
        }}
        onMouseEnter={() => !showLoadingUI && setHovered(true)}
        onMouseLeave={() => !showLoadingUI && setHovered(false)}
      >
        {isPending && (
          <Flex
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: '#FFF8EA',
              padding: `4px 20px`,
              borderRadius: '14px',
              width: '92px',
              height: '28px',
              alignItems: 'center',
              cursor: 'default'
            }}
          >
            <Text variant="subtitle2" color="primary">
              Pending
            </Text>
          </Flex>
        )}
        <Box>
          <Box padding={theme.spacing.md} display="flex" flexDirection="column" gap={theme.spacing.md}>
            <SlRow horizontalPosition="space-between" verticalPosition="center">
              <ElementCard
                textSx={{ fontSize: '20px', fontWeight: 'bold' }}
                color={isTopicSegment ? 'accentTangerine' : 'info'}
                variant="small"
              >
                {`${(displayName[0] || '').toUpperCase()}${(displayName[1] || '').toLowerCase()}`}
              </ElementCard>
              {hovered && !showLoadingUI && (
                <SlRow spacing="sm">
                  {userId === segmentUserId && (
                    <Link
                      to={`/search${searchParams}&doAggregation=true&entityType=${
                        savedSearch.entityType
                      }&${queryString.stringify(extractQueryConditions(savedSearch, true, true))}&action=edit`}
                    >
                      {isTopicSegment ? (
                        <TopicsMiniButton>Edit</TopicsMiniButton>
                      ) : (
                        <SegmentMiniButton>Edit</SegmentMiniButton>
                      )}
                    </Link>
                  )}
                  {userId === segmentUserId && (
                    <Text onClick={() => handleDelete()}>
                      {isTopicSegment ? (
                        <TopicsMiniButton>Delete</TopicsMiniButton>
                      ) : (
                        <SegmentMiniButton>Delete</SegmentMiniButton>
                      )}
                    </Text>
                  )}
                  <Link
                    to={`/search${searchParams}&entityType=${savedSearch.entityType}&${queryString.stringify(
                      extractQueryConditions(savedSearch, true)
                    )}&action=create`}
                  >
                    {isTopicSegment ? (
                      <TopicsMiniButton>Duplicate</TopicsMiniButton>
                    ) : (
                      <SegmentMiniButton>Duplicate</SegmentMiniButton>
                    )}
                  </Link>
                </SlRow>
              )}
            </SlRow>
            <Box display="flex" flexDirection="column" gap={theme.spacing.xs}>
              {/* This getAppName() === AppName.Atlas temp flag until we implement doris for topics */}
              {showLoadingUI && getAppName() === AppName.Atlas && !isTopicSegment ? (
                <div style={{ cursor: 'default' }}>
                  <SegmentCardTitle title={displayName}>{displayName}</SegmentCardTitle>
                </div>
              ) : (
                <Link to={buildSegmentUrl()}>
                  <SegmentCardTitle title={displayName}>{displayName}</SegmentCardTitle>
                </Link>
              )}
              <Text variant="body2">{subtitle}</Text>
            </Box>
          </Box>
          <Box sx={{ marginTop: '40px' }}>
            {showLoadingUI ? (
              <div style={{ width: '100%', height: 115 }} />
            ) : (
              <SplineChartV2
                primarySeries={primarySeries}
                comparisonSeries={comparisonSeries}
                metricType={metricType}
                removeSpacing
                preview={{ width: +BEACON_PRO_ENTITY_CARD_WIDTH.replace('px', ''), height: 115 }}
                {...categoryOverrides}
                chartOptionsOverride={{
                  chart: {
                    marginLeft: -2
                  },
                  plotOptions: {
                    series: {
                      lineWidth: 2
                    }
                  }
                }}
              />
            )}
          </Box>
        </Box>
        <div>
          <SlColumn spacing="xxs" verticalInset="md" horizontalInset="md">
            <Text
              sx={{ display: 'flex', height: '24px', alignItems: 'center' }}
              variant="subtitle2"
              transform="uppercase"
            >
              {metricDisplayName}
            </Text>
            {showLoadingUI ? (
              <SlSkeleton variant="text" width={120} height={20} />
            ) : (
              <SlRow verticalPosition="center" spacing="sm">
                <Text variant="body1">{metricTotal}</Text>
                <SlRow verticalPosition="center" spacing="xs">
                  {getIconForMetricChange(percentChange, 2)}
                  <Text variant="subtitle2" color={getColorForMetricChangeValue(percentChange)}>
                    {`${formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, {
                      decimalPlaces: 2,
                      showNegative: false
                    })}`}
                  </Text>
                </SlRow>
              </SlRow>
            )}
          </SlColumn>
        </div>
      </div>
    );
  }
);

SegmentCard.displayName = 'SegmentCard';

interface SmartSegmentCardProps {
  segment: Segment;
  indexName?: string;
  fieldName?: string;
  metricDisplayName?: string;
  color?: StacklineColor;
  isTopicSegment?: boolean;
  isPending?: boolean;
  handleDelete?: () => void;
}

/**
 * Atlas-specific segment card component that uses GetSalesMetrics API
 * Now uses SplineChartV2 format with weekId-based data
 */
export const AtlasSmartSegmentCard = ({
  segment,
  fieldName = 'retailSales',
  metricDisplayName = 'Retail Sales',
  handleDelete,
  isTopicSegment,
  isPending = false
}: SmartSegmentCardProps) => {
  const { adapter, isLoading, hasValidData } = useAtlasSegmentData({ segment, fieldName, isPending });
  
  // Get comparison week IDs for category overrides
  const { isYTDWithPriorYear, comparisonStartWeekId, comparisonEndWeekId } = useComparisonWeekIds();

  // Show loading UI when either pending or loading data
  const showLoadingUI = isPending || isLoading;

  // Calculate category overrides for YTD + Prior Year mode
  const categoryOverrides = useMemo(() => {
    if (isYTDWithPriorYear && comparisonStartWeekId && comparisonEndWeekId) {
      const comparisonYear = yearPartOfWeekId(comparisonStartWeekId);
      return {
        categoryStartWeekId: comparisonStartWeekId,
        categoryEndWeekId: getLastWeekIdOfYear(comparisonYear)
      };
    }
    return {};
  }, [isYTDWithPriorYear, comparisonStartWeekId, comparisonEndWeekId]);

  // For loading/pending segments, use default values
  let chartData: { primarySeries: SplineChartV2Series; comparisonSeries: SplineChartV2Series | undefined } = {
    primarySeries: { id: 'primary', name: 'Primary', data: [] },
    comparisonSeries: undefined
  };
  let legendData = {
    percentChange: 0,
    formattedPrimaryTotal: null as string | null,
    metricType: METRICTYPE.VOLUME
  };

  // Only transform data if not loading/pending and has valid data
  if (!showLoadingUI && hasValidData) {
    const transformedData = adapter.transformV2();
    chartData = transformedData.chartData;
    legendData = transformedData.legendData;
  }

  return (
    <SegmentCard
      savedSearch={segment?.segment || null}
      segmentId={segment?.id || ''}
      displayName={segment?.displayName || 'Unknown Segment'}
      percentChange={legendData.percentChange}
      subtitle={`Created by ${segment?.segment?.owner?.firstName || 'Unknown'} ${
        segment?.segment?.owner?.lastName || 'User'
      }`}
      metricTotal={legendData.formattedPrimaryTotal}
      primarySeries={chartData.primarySeries}
      comparisonSeries={chartData.comparisonSeries}
      categoryStartWeekId={categoryOverrides.categoryStartWeekId}
      categoryEndWeekId={categoryOverrides.categoryEndWeekId}
      segmentUserId={segment?.userId || ''}
      metricDisplayName={metricDisplayName}
      metricType={legendData.metricType}
      isTopicSegment={isTopicSegment}
      isPending={isPending}
      isLoading={isLoading}
      handleDelete={handleDelete}
    />
  );
};

/**
 * Beacon-specific segment card component that uses AdvancedSearch API with useMetricByWeekIdV2
 * Returns weekId-based data that works correctly with SplineChartV2
 */
export const BeaconSmartSegmentCard = React.memo(
  ({
    segment,
    indexName = 'sales',
    fieldName = 'retailSales',
    metricDisplayName = 'Retail Sales',
    handleDelete,
    isTopicSegment,
    isPending = false
  }: SmartSegmentCardProps) => {
    const requestBuilder = useCallback(
      (body: AdvancedSearchRequestBuilder) => {
        body.clearConditionRangeFilters().clearConditionTermFilters();

        if (segment.conditions.rangeFilters) {
          segment.conditions.rangeFilters.forEach((rangeFilter) => {
            body.addConditionRangeFilter(rangeFilter.fieldName, rangeFilter.minValue, rangeFilter.maxValue);
          });
        }

        if (segment.conditions.termFilters) {
          segment.conditions.termFilters.forEach((termFilter) => {
            body.addConditionTermFilter(termFilter.fieldName, termFilter.condition, termFilter.values);
          });
        }
        return body;
      },
      [segment]
    );

    const {
      formattedPrimaryTotal,
      percentChange,
      isLoading,
      primarySeries,
      comparisonSeries,
      metricType,
      isYTDWithPriorYear,
      comparisonStartWeekId,
      comparisonEndWeekId
    } = useMetricByWeekIdV2({
      indexName,
      fieldName,
      requestBuilder
    });

    // Show loading UI when either pending or loading data
    const showLoadingUI = isPending || isLoading;

    // Calculate category overrides for YTD + Prior Year mode
    const categoryOverrides = useMemo(() => {
      if (isYTDWithPriorYear && comparisonStartWeekId && comparisonEndWeekId) {
        const comparisonYear = yearPartOfWeekId(comparisonStartWeekId);
        return {
          categoryStartWeekId: comparisonStartWeekId,
          categoryEndWeekId: getLastWeekIdOfYear(comparisonYear)
        };
      }
      return {};
    }, [isYTDWithPriorYear, comparisonStartWeekId, comparisonEndWeekId]);

    // For loading/pending segments, use default values
    const displayData = showLoadingUI
      ? {
          formattedPrimaryTotal: null,
          percentChange: 0,
          primarySeries: { id: 'primary', name: 'Primary', data: [] },
          comparisonSeries: undefined,
          metricType: metricType || METRICTYPE.VOLUME
        }
      : {
          formattedPrimaryTotal,
          percentChange,
          primarySeries,
          comparisonSeries,
          metricType
        };

    return (
      <SegmentCard
        savedSearch={segment.segment}
        segmentId={segment.id}
        displayName={segment.displayName}
        percentChange={displayData.percentChange}
        subtitle={`Created by ${segment.segment.owner.firstName} ${segment.segment.owner.lastName}`}
        metricTotal={displayData.formattedPrimaryTotal}
        primarySeries={displayData.primarySeries}
        comparisonSeries={displayData.comparisonSeries}
        categoryStartWeekId={categoryOverrides.categoryStartWeekId}
        categoryEndWeekId={categoryOverrides.categoryEndWeekId}
        segmentUserId={segment?.userId || ''}
        metricDisplayName={metricDisplayName}
        metricType={displayData.metricType}
        isTopicSegment={isTopicSegment}
        isPending={isPending}
        isLoading={isLoading}
        handleDelete={handleDelete}
      />
    );
  }
);

BeaconSmartSegmentCard.displayName = 'BeaconSmartSegmentCard';

/**
 * Smart wrapper that determines which segment card implementation to use
 * - Atlas: Uses GetSalesMetrics API (new)
 * - Beacon: Uses AdvancedSearch API (legacy, will be deprecated)
 */
export const SmartSegmentCard = React.memo((props: SmartSegmentCardProps) => {
  const isAtlasDoris = getAppName() === AppName.Atlas && !props.isTopicSegment;

  if (isAtlasDoris) {
    return <AtlasSmartSegmentCard {...props} />;
  } else {
    return <BeaconSmartSegmentCard {...props} />;
  }
});

SmartSegmentCard.displayName = 'SmartSegmentCard';

export default SegmentCard;
